import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { IMapperEntity, IMapperTable, SchemaLoadStatus } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { MapperConnection } from 'app/model/adfDataMapper/mapperConnection';
import { AzureBlobStorageLocation } from 'app/model/dataset/location/azure-blob.location';
import { AzureBlobFSLocation } from 'app/model/dataset/location/azure-blobfs.location';
import { OrcDataset } from 'app/model/dataset/orc.dataset';
import { XmlDataset } from 'app/model/dataset/xml.dataset';
import {
    DelimitedTextDataset,
    ParquetDataset,
    AvroDataset,
    JsonDataset,
    FilePath,
    LinkedService,
    DeltaDataset,
    Dataset,
    LakehouseTableDataset
} from 'shell-models-lib';
import { ParquetInlineSettings } from 'app/model/dataflow/dataset-settings/parquet-inline-settings';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { AvroSettings } from 'app/model/dataflow/dataset-settings/avro-settings';
import { JsonSettings } from 'app/model/dataflow/dataset-settings/json-settings';
import { XmlSettings } from 'app/model/dataflow/dataset-settings/xml-settings';
import { OrcSettings } from 'app/model/dataflow/dataset-settings/orc-settings';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setCommonPropertiesBasedOnBlobBrowsedPath(selectedLinkedService: LinkedService, tempDB: any, path: FilePath) {
    tempDB.linkedService = selectedLinkedService;
    tempDB.location = AzureBlobStorageLocation.MODEL.create<AzureBlobStorageLocation>();
    tempDB.location.setLocation(path);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setCommonPropertiesBasedOnADLSBrowsedPath(selectedLinkedService: LinkedService, tempDB: any, path: FilePath) {
    tempDB.linkedService = selectedLinkedService;
    tempDB.location = AzureBlobFSLocation.MODEL.create<AzureBlobFSLocation>();
    tempDB.location.setLocation(path);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setInitialPropertiesForBlob(selectedLinkedService: LinkedService, tempDB: any) {
    tempDB.linkedService = selectedLinkedService;
    tempDB.location = AzureBlobStorageLocation.MODEL.create<AzureBlobStorageLocation>();
    tempDB.location.setLocation(new FilePath());
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setInitialPropertiesForADLS(selectedLinkedService: LinkedService, tempDB: any) {
    tempDB.linkedService = selectedLinkedService;
    tempDB.location = AzureBlobFSLocation.MODEL.create<AzureBlobFSLocation>();
    tempDB.location.setLocation(new FilePath());
}

export function insertRecordWithDefaultProperties(
    selectedADLSFormat: DatasetFormatType,
    selectedLinkedService: LinkedService
) {
    const tempDB = fetchDatasetBasedOnSelectedADLSFormat(selectedADLSFormat);
    if (selectedLinkedService.getLinkedServiceType() === 'AzureBlobStorage') {
        setInitialPropertiesForBlob(selectedLinkedService, tempDB);
    } else {
        setInitialPropertiesForADLS(selectedLinkedService, tempDB);
    }
    return {
        entityName: '',
        dataset: tempDB
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fetchLakehouseTableDataset(tableName: string, selectedLinkedService: LinkedService): any {
    const lakehouseDataset =  new LakehouseTableDataset('LakehouseTableDataset');
    lakehouseDataset.linkedService = selectedLinkedService;
    lakehouseDataset.table = tableName;
    return lakehouseDataset;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function fetchDatasetBasedOnSelectedADLSFormat(selectedADLSFormat: DatasetFormatType, selectedFileSettings?: DataFlowDatasetSettings): any {
    switch (selectedADLSFormat) {
        case DatasetFormatType.Delimited: {
            const tempDB = new DelimitedTextDataset('Delimited');
            tempDB.firstRowAsHeader = true;
            if (!!selectedFileSettings) {
                const delimitedettings = selectedFileSettings as DelimitedTextSettings;
                tempDB.compressionCodec = delimitedettings.compressionType;
                tempDB.compressionLevel = delimitedettings.compressionLevel.expression;
                tempDB.encodingName = delimitedettings.encoding.expression;
                tempDB.columnDelimiter = delimitedettings.columnDelimiter.expression;
                tempDB.rowDelimiter = delimitedettings.rowDelimiter.expression;
                tempDB.quoteChar = delimitedettings.quoteCharacter.expression;
                tempDB.escapeChar = delimitedettings.escapeCharacter.expression;
            }
            return tempDB;
        }
        case DatasetFormatType.Parquet:
            const parquetDataset = new ParquetDataset('Parquet');
            if (!!selectedFileSettings) {
                const parquetSettings = selectedFileSettings as ParquetInlineSettings;
                parquetDataset.compressionCodec = parquetSettings.compressionType;
            }
            return parquetDataset;
        case DatasetFormatType.Avro:
            const avroDataset = new AvroDataset('Avro');
            if (!!selectedFileSettings) {
                const avroSettings = selectedFileSettings as AvroSettings;
                avroDataset.compressionCodec = avroSettings.compressionType;
            }
            return avroDataset;
        case DatasetFormatType.Json:
            const jsonDataset = new JsonDataset('Json');
            if (!!selectedFileSettings) {
                const jsonSettings = selectedFileSettings as JsonSettings;
                jsonDataset.compressionCodec = jsonSettings.compressionType;
                jsonDataset.compressionLevel = jsonSettings.compressionLevel.expression;
                jsonDataset.encodingName = jsonSettings.encoding.expression;
            }
            return jsonDataset;
        case DatasetFormatType.Orc:
            const orcDataset = new OrcDataset('Orc');
            if (!!selectedFileSettings) {
                const orcSettings = selectedFileSettings as OrcSettings;
                orcDataset.compressionCodec = orcSettings.compressionType;
            }
            return orcDataset;
        case DatasetFormatType.Xml:
            const xmlDataset = new XmlDataset('Xml');
            if (!!selectedFileSettings) {
                const xmlSettings = selectedFileSettings as XmlSettings;
                xmlDataset.compressionCodec = xmlSettings.compressionType;
                xmlDataset.compressionLevel = xmlSettings.compressionLevel.expression;
                xmlDataset.encodingName = xmlSettings.encoding.expression;
            }
            return xmlDataset;
        case DatasetFormatType.Delta:
            return new DeltaDataset('Delta');
        default:
            return undefined;
    }
}

export function processEachADLSEntity(
    filePath: FilePath,
    selectedLinkedService: LinkedService,
    elem: IMapperEntity,
    selectedDatasetFormat: DatasetFormatType,
    tables: IMapperTable[],
    isSource: Boolean): IMapperTable[] {

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dslConnectorProperties: Map<string, any> = new Map([
        [DslConnectorConstants.container, filePath.container],
        [DslConnectorConstants.fileSystem, filePath.container],
    ]);

    if (filePath.folderPath) {
        dslConnectorProperties.set(DslConnectorConstants.folderPath, filePath.folderPath);
    }

    tables.push({
        // need a valid mapping dataflow source name here to pass dataflow validations
        name: getValidTransformationName(isSource, elem.entityName),
        sourceDataset: selectedDatasetFormat !== DatasetFormatType.Delta ? elem.dataset : undefined,
        sourceLinkedService: selectedLinkedService,
        isInlineDataset: true,
        // will initialize datasetSettings post processing each Source|Entity adls-entity.
        datasetSettings: undefined,
        wildcardPaths: [],
        dslConnectorProperties,
        datasetType: selectedDatasetFormat,
        schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
        tableName: elem.entityName
    });

    return tables;
}

export function processEachDeltaLakehouseEntity(
    selectedLinkedService: LinkedService,
    elem: IMapperEntity,
    selectedDatasetFormat: DatasetFormatType,
    tables: IMapperTable[],
    isSource: Boolean): IMapperTable[] {

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dslConnectorProperties: Map<string, any> = new Map([
        [ 'tableName', elem.entityName],
        [ DslConnectorConstants.container, 'container'],
        [ DslConnectorConstants.fileSystem, 'fileSystem'],
    ]);

    tables.push({
        // need a valid mapping dataflow source name here to pass dataflow validations
        name: getValidTransformationName(isSource, elem.entityName),
        sourceDataset : selectedDatasetFormat !== DatasetFormatType.Delta ? elem.dataset : undefined,
        sourceLinkedService: selectedLinkedService,
        isInlineDataset: true,
        // will initialize datasetSettings post processing each Source|Entity adls-entity.
        datasetSettings: undefined,
        wildcardPaths: [],
        dslConnectorProperties,
        datasetType: selectedDatasetFormat,
        schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
        tableName: elem.entityName
    });

    return tables;
}


export function ensureAllPathsAreUnique(
    filePaths: IMapperEntity[]
): boolean {
    const seenPaths = new Set<string>();
    for (const path of filePaths) {
        if (seenPaths.has(path.entityName)) {
            return false;
        }
        seenPaths.add(path.entityName);
    }
    return true;
}

export function getAdlsDatasetForPreview(datasetFormat: DatasetFormatType, container: string, folderPath: string, linkedService: LinkedService, selectedSourceFileOptions?: DataFlowDatasetSettings): Dataset {
    if (!!datasetFormat && !!container && !!linkedService) {
        const path = new FilePath();
        path.container = container;
        path.folderPath = folderPath;
        const dataset = fetchDatasetBasedOnSelectedADLSFormat(datasetFormat, selectedSourceFileOptions);
        if (linkedService.getLinkedServiceType() === 'AzureBlobStorage') {
            setCommonPropertiesBasedOnBlobBrowsedPath(linkedService, dataset, path);
        } else {
            setCommonPropertiesBasedOnADLSBrowsedPath(linkedService, dataset, path);
        }

        return dataset;
    }

    return undefined;
}

export function initializeCommonConnectionProperties(
    connection: MapperConnection,
    selectedDatasetFormat: DatasetFormatType,
    isSource: Boolean): void {

    if (!!connection) {
        const commonDslConnectorProperties = connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, selectedDatasetFormat);

        if (isSource) {
            commonDslConnectorProperties.set(DslConnectorConstants.dateFormats, DslConnectorConstants.dateFormatOptions);
            commonDslConnectorProperties.set(DslConnectorConstants.timestampFormats, DslConnectorConstants.timestampFormatOptions.map((v) => DataFlowExpressionUtils.escapeSingleQuoteInDslString(v)));
        }

        if (selectedDatasetFormat !== DatasetFormatType.Delta) {
            if (isSource) {
                commonDslConnectorProperties.set('enableCdc', true);
                commonDslConnectorProperties.set('skipInitialLoad', true);
            }
            if (!isSource && selectedDatasetFormat === DatasetFormatType.Delimited) {
                commonDslConnectorProperties.set(DslConnectorConstants.columnNamesAsHeader, true);
                commonDslConnectorProperties.set(DslConnectorConstants.columnDelimiter, ',');
                commonDslConnectorProperties.set(DslConnectorConstants.escapeChar, '\\\\');
                commonDslConnectorProperties.set(DslConnectorConstants.quoteChar, '\\\"');
            }
        } else if (selectedDatasetFormat === DatasetFormatType.Delta) {
            commonDslConnectorProperties.set('mergeSchema', true);
            commonDslConnectorProperties.set('autoCompact', true);
            commonDslConnectorProperties.set('optimizedWrite', true);
            commonDslConnectorProperties.set(DslConnectorConstants.deletable, false);
            commonDslConnectorProperties.set(DslConnectorConstants.insertable, true);
            commonDslConnectorProperties.set(DslConnectorConstants.updateable, false);
            commonDslConnectorProperties.set(DslConnectorConstants.upsertable, false);
            commonDslConnectorProperties.set('skipDuplicateMapInputs', true);
            commonDslConnectorProperties.set('skipDuplicateMapOutputs', true);
        }
    }
}
